import { useState, useEffect } from 'react';
import { useScrollReveal, animStyle } from '../../../hooks/useScrollReveal';

interface Service {
  title: string;
  shortDesc: string;
  icon: string;
  detail: string;
  features: string[];
  examples: string[];
}

const services: Service[] = [
  {
    title: 'ソーシャルメディア各種運用',
    icon: 'ri-share-line',
    shortDesc: 'Facebook、Twitter、Instagram、YouTubeなど各SNSの戦略的な運用・管理をワンストップで提供します。',
    detail:
      '各SNSプラットフォームの特性を熟知したプロフェッショナルが、貴社のブランドに最適な運用戦略を立案・実行します。アカウント開設から日々の投稿管理、フォロワー獲得施策、エンゲージメント向上まで、ソーシャルメディアに関わるすべての業務をワンストップでサポートします。',
    features: [
      'アカウント開設・プロフィール最適化',
      '月間コンテンツカレンダー作成・管理',
      '投稿文・ハッシュタグ企画・制作',
      'フォロワー獲得・エンゲージメント向上施策',
      '月次パフォーマンスレポート提出',
      'コメント・DM対応（モデレーション）',
    ],
    examples: ['Facebook', 'X (Twitter)', 'Instagram', 'YouTube', 'TikTok', 'LINE公式アカウント', 'LinkedIn'],
  },
  {
    title: '各種広告運用',
    icon: 'ri-bar-chart-2-line',
    shortDesc: 'SNS広告・リスティング広告・ディスプレイ広告など、目的に合った最適な広告施策をご提案します。',
    detail:
      '豊富な広告運用実績を持つスペシャリストが、貴社のKPIに合わせた最適な広告戦略を設計します。配信設定から効果測定・改善まで一貫して対応し、費用対効果の最大化を追求します。データドリブンなPDCAサイクルにより、継続的な広告パフォーマンス向上を実現します。',
    features: [
      'SNS広告（Facebook/Instagram/X/LINE）',
      'Google・Yahoo! リスティング広告',
      'ディスプレイ広告・リターゲティング',
      'クリエイティブ制作・A/Bテスト',
      '広告予算の最適配分・管理',
      '効果測定レポート・改善提案',
    ],
    examples: ['Facebook Ads', 'Instagram Ads', 'Google Ads', 'Yahoo!広告', 'LINE Ads', 'Twitter Ads'],
  },
  {
    title: 'コンテンツ制作',
    icon: 'ri-film-line',
    shortDesc: '映像・写真・記事・グラフィックなど、ブランドの魅力を最大限に引き出すコンテンツを制作します。',
    detail:
      'ブランドの世界観を深く理解したクリエイターチームが、SNS・Web・広告など各媒体に最適化されたコンテンツを制作します。企画立案からディレクション、撮影・編集・納品まで一貫して対応。ターゲット層の心に刺さる高品質なコンテンツで、エンゲージメントと認知拡大を同時に実現します。',
    features: [
      '動画・リール・ショート動画制作',
      '商品・人物・スポットフォト撮影',
      'ブログ・SNSキャプション記事執筆',
      'バナー・インフォグラフィック制作',
      'ブランドガイドライン策定',
      'ライブ配信企画・サポート',
    ],
    examples: ['プロモーション映像', 'SNS投稿用画像', 'ブランドムービー', 'インタビュー記事', 'インフォグラフィック', 'ライブ配信'],
  },
  {
    title: 'イベント・キャンペーン企画・運営',
    icon: 'ri-calendar-event-line',
    shortDesc: 'オンライン・オフラインを問わず、効果的なイベントやキャンペーンの企画から運営まで総合的にサポートします。',
    detail:
      'ソーシャルメディアを最大限に活用したキャンペーン・イベントの企画・設計から当日の運営まで、プロフェッショナルチームが総合的にサポートします。ハッシュタグキャンペーンやUGC促進施策など、拡散力を高めた施策設計で、ブランドの認知拡大とエンゲージメント向上を同時に実現します。',
    features: [
      'キャンペーン企画・レギュレーション設計',
      'ハッシュタグ・UGCキャンペーン運営',
      'プレゼント・懸賞キャンペーン実施',
      'オンライン/オフラインイベント運営',
      '当選者管理・景品発送対応',
      '効果測定・レポーティング',
    ],
    examples: ['Twitterキャンペーン', 'Instagramプレゼント', 'ライブイベント中継', 'ファンミーティング', 'ポップアップストア', 'ウェビナー運営'],
  },
];

interface ServiceModalProps {
  service: Service | null;
  onClose: () => void;
}

function ServiceModal({ service, onClose }: ServiceModalProps) {
  useEffect(() => {
    if (!service) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [service, onClose]);

  if (!service) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-2xl bg-[#111] border border-white/15 overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'modalIn 0.25s ease-out' }}
      >
        <div className="relative px-8 pt-10 pb-6 border-b border-white/10">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center border border-white/30 shrink-0 mt-0.5">
              <i className={`${service.icon} text-lg text-white`} />
            </div>
            <h2 className="text-xl md:text-2xl font-medium text-white leading-snug tracking-wider">
              {service.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="px-8 py-8 space-y-8">
          <p className="text-white/75 text-sm leading-loose">{service.detail}</p>
          <div>
            <h3 className="text-white text-xs tracking-[0.2em] uppercase mb-4 flex items-center gap-2">
              <span className="w-4 h-px bg-white inline-block" />
              主なサービス内容
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {service.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
                  <i className="ri-arrow-right-s-line text-white/40 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white text-xs tracking-[0.2em] uppercase mb-4 flex items-center gap-2">
              <span className="w-4 h-px bg-white inline-block" />
              対応プラットフォーム・実績例
            </h3>
            <div className="flex flex-wrap gap-2">
              {service.examples.map((ex, i) => (
                <span key={i} className="text-xs text-white/60 border border-white/20 px-3 py-1 tracking-wider">
                  {ex}
                </span>
              ))}
            </div>
          </div>
          <div className="pt-2">
            <button
              onClick={() => {
                onClose();
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              }}
              className="w-full sm:w-auto bg-white text-black text-xs tracking-widest px-10 py-3 hover:bg-white/80 transition-colors cursor-pointer whitespace-nowrap"
            >
              このサービスについて問い合わせる
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default function Services() {
  const [activeService, setActiveService] = useState<Service | null>(null);
  const { ref: headerRef, visible: headerVisible } = useScrollReveal(0.3);
  const { ref: gridRef, visible: gridVisible } = useScrollReveal(0.15);

  return (
    <>
      <section id="services" className="relative w-full" style={{ minHeight: '500px' }}>
        <div className="absolute inset-0">
          <img
            src="https://static.wixstatic.com/media/226be6d6c70c41d2952d53c8a529f73b.jpeg/v1/fill/w_1280,h_498,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/226be6d6c70c41d2952d53c8a529f73b.jpeg"
            alt="services background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 w-full px-5 sm:px-8 md:px-20 py-14 md:py-20">
          {/* Section header */}
          <div ref={headerRef} className="mb-10 md:mb-12">
            <div
              style={{
                width: headerVisible ? '32px' : '0px',
                height: '2px',
                backgroundColor: '#fff',
                marginBottom: '24px',
                transition: 'width 0.7s ease-out 0ms',
              }}
            />
            <h2
              className="text-5xl md:text-6xl font-bold text-white tracking-widest font-['Montserrat']"
              style={animStyle(headerVisible, 'fromLeft', 150, 750)}
            >
              SERVICES
            </h2>
          </div>

          {/* Services grid */}
          <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
            {services.map((service, idx) => (
              <button
                key={idx}
                onClick={() => setActiveService(service)}
                className="text-left border-l border-white/30 pl-5 sm:pl-6 pr-4 py-5 md:py-6 group hover:border-white hover:bg-white/5 transition-all duration-300 cursor-pointer"
                style={animStyle(gridVisible, 'up', idx * 120, 700)}
              >
                <div className="w-8 h-8 flex items-center justify-center border border-white/30 group-hover:border-white mb-3 md:mb-4 transition-colors duration-300">
                  <i className={`${service.icon} text-sm text-white`} />
                </div>
                <h3 className="text-white text-sm font-medium tracking-wider mb-2 md:mb-3 leading-relaxed">
                  {service.title}
                </h3>
                <p className="text-white/50 text-xs leading-relaxed group-hover:text-white/75 transition-colors duration-300 mb-3 md:mb-4">
                  {service.shortDesc}
                </p>
                <span className="inline-flex items-center gap-1 text-white/40 text-xs tracking-widest group-hover:text-white/70 transition-colors duration-300">
                  詳細を見る
                  <i className="ri-arrow-right-line text-xs" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <ServiceModal service={activeService} onClose={() => setActiveService(null)} />
    </>
  );
}
